import { useTranslation } from "react-i18next";
import { Badge, Label, Textarea, cn } from "@nexus/ui";
import { AlertTriangle, FileText } from "lucide-react";

interface TermsFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Current published version — server-owned, shown read-only. Undefined when creating. */
  currentVersion?: number;
  /** True once the admin has changed the terms text on an existing vendor. */
  isDirty: boolean;
  disabled?: boolean;
  /** Translated validation message; renders an inline error and flags the field. */
  error?: string;
}

/**
 * Terms & Conditions editor.
 *
 * `termsVersion` is **server-owned**: the API auto-bumps it when the terms text
 * changes *and* at least one tenant has already signed the current version
 * (forcing those tenants to re-sign). It is therefore surfaced read-only here —
 * never an input — with an explicit warning so the admin understands the
 * downstream re-sign consequence before publishing an edit.
 */
export function TermsField({
  value,
  onChange,
  onBlur,
  currentVersion,
  isDirty,
  disabled,
  error,
}: TermsFieldProps) {
  const { t } = useTranslation();
  const isEdit = currentVersion != null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor="vf-terms" className="flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          {t("marketplace.admin.form.termsMarkdown")}
          <span className="text-sm text-destructive" aria-hidden>
            *
          </span>
        </Label>
        {isEdit && (
          <Badge variant="slate" className="font-mono text-[10px]" aria-live="polite">
            {t("marketplace.admin.form.termsVersionBadge", {
              version: currentVersion,
              defaultValue: "Version {{version}}",
            })}
          </Badge>
        )}
      </div>

      <Textarea
        id="vf-terms"
        rows={8}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? "vf-terms-error" : undefined}
        className="resize-y font-mono text-xs leading-relaxed"
        placeholder={t("marketplace.admin.form.termsPlaceholder", {
          defaultValue: "# Terms & Conditions\n\nAdd the agreement tenants must accept before connecting…",
        })}
      />

      {error ? (
        <p id="vf-terms-error" role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t("marketplace.admin.form.termsHint", {
            defaultValue:
              "Markdown supported. Tenants review and sign this text when they connect.",
          })}
        </p>
      )}

      {isEdit && isDirty && (
        <div
          className={cn(
            "flex items-start gap-2 rounded-md border border-warning/30 bg-warning-subtle px-3 py-2",
            "text-xs text-warning-subtle-foreground",
          )}
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p>
            {t("marketplace.admin.form.termsResignWarning", {
              defaultValue:
                "Editing published terms publishes a new version. Tenants who already signed will be asked to re-sign before they can send new scripts.",
            })}
          </p>
        </div>
      )}
    </div>
  );
}
